import { createContext } from 'react';

import type { AuthenticatedUser } from './session';

/**
 * Where the application is in working out who the visitor is.
 *
 * `offline` means the server could not be asked, and the user shown is the one remembered from the
 * last reachable session: enough to open field mode's queued work, never a grant of access.
 */
export type AuthStatus = 'resolving' | 'authenticated' | 'anonymous' | 'offline';

/** The authentication state and the operations that change it. */
export interface AuthContextValue {
  /** Whether the session has been resolved yet, and what the answer was. */
  readonly status: AuthStatus;
  /** The signed-in user, or null when nobody is signed in. */
  readonly user: AuthenticatedUser | null;
  /** Signs in and stores the resulting session. */
  readonly signIn: (email: string, password: string, workspace?: string) => Promise<void>;
  /** Signs out and discards the session. */
  readonly signOut: () => Promise<void>;
  /**
   * Whether the signed-in user holds a capability.
   *
   * Advisory only: it decides what to render, never what is allowed. The server authorises every
   * request independently, so a client that got this wrong would show a button that 403s rather
   * than grant anything.
   */
  readonly can: (permission: string) => boolean;
  /**
   * A stable string identifying the acting principal (user and workspace), or null when signed out.
   *
   * Cached data belongs to a principal, not to a page. Including this in a query key is what keeps
   * one workspace's answers from being served to the next session in the same tab.
   */
  readonly identity: string | null;
}

/**
 * Undefined outside a provider, on purpose: {@code useAuth} turns that into a clear error rather
 * than silently handing a component a default "nobody is signed in" value that looks plausible.
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
